import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  PlayIcon,
  ArrowTopRightOnSquareIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  getResearchAgent,
  updateResearchAgent,
  runResearchAgent,
  listKnowledgeItems,
  listResearchRuns,
  listResearchSources,
  approveKnowledgeItem,
  rejectKnowledgeItem,
} from "../../api/adminApi";

const Card = ({ children, className = "" }) => (
  <div className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}>
    {children}
  </div>
);

const STATUS_STYLES = {
  active: "bg-emerald-500/15 text-emerald-500",
  proposed: "bg-amber-500/15 text-amber-500",
  superseded: "bg-gray-500/15 text-gray-400",
  rejected: "bg-red-500/15 text-red-500",
  success: "bg-emerald-500/15 text-emerald-500",
  error: "bg-red-500/15 text-red-500",
  running: "bg-sky-500/15 text-sky-500",
};

const Badge = ({ children, kind }) => (
  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[kind] || "bg-gray-500/15 text-gray-400"}`}>
    {children}
  </span>
);

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

const TABS = ["Knowledge", "Runs", "Sources", "Config"];

// A real, clickable source vs a placeholder/empty one we shouldn't link.
const isRealUrl = (u) =>
  !!u && /^https?:\/\//i.test(u) && !/(^|\.)example\.(com|org|net|gov\.in)\b/i.test(u);

const KnowledgeItemCard = ({ it, onDecide, busy }) => {
  const [open, setOpen] = useState(false);
  const real = isRealUrl(it.source_url);
  // Approve shown for anything not already live; reject/revoke shown for
  // anything not already rejected. So: proposed → Approve+Reject,
  // active → Revoke, superseded/rejected → re-activate options.
  const canApprove = it.status !== "active";
  const canReject = it.status !== "rejected";
  const approveLabel = it.status === "proposed" ? "Approve" : "Re-activate";
  const rejectLabel = it.status === "active" ? "Revoke" : "Reject";
  return (
    <Card>
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold leading-snug">{it.title}</h3>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge kind={it.status}>{it.status}</Badge>
            <span className="text-[10px] text-gray-500">{it.jurisdiction}</span>
          </div>
        </div>
        {it.summary && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{it.summary}</p>}
        <p className="text-[11px] text-gray-500 mt-1.5">
          eff {it.effective_date || "current"} · confidence {it.confidence}% · via {it.source_type}
          {" · "}
          <span className="text-indigo-500">{open ? "hide report ▲" : "show report ▼"}</span>
        </p>
      </button>

      {open && (
        <div className="mt-3 p-3 rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/10">
          <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-1">
            Guidance the specialist uses
          </p>
          <p className="text-sm whitespace-pre-wrap">
            {it.body || it.summary || "(no detailed body)"}
          </p>
          <div className="mt-2 text-xs">
            {real ? (
              <a href={it.source_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-indigo-500 hover:underline">
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                {it.source_title || it.source_url}
              </a>
            ) : (
              <span className="text-gray-500">
                Source: {it.source_title || "—"}
                {it.source_url ? ` (${it.source_url} — not a live link)` : " (no link)"}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Decision controls — work on any status so you can revoke after approving */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-white/10">
        {it.status === "active" && (
          <span className="text-[11px] text-emerald-500 mr-auto">Live to specialists</span>
        )}
        {canApprove && (
          <button
            disabled={busy}
            onClick={() => onDecide(it.id, "approve")}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50"
          >
            <CheckIcon className="w-4 h-4" /> {approveLabel}
          </button>
        )}
        {canReject && (
          <button
            disabled={busy}
            onClick={() => onDecide(it.id, "reject")}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 ${
              it.status === "active"
                ? "bg-red-600 hover:bg-red-500 text-white"
                : "bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20"
            }`}
          >
            <XMarkIcon className="w-4 h-4" /> {rejectLabel}
          </button>
        )}
      </div>
    </Card>
  );
};

const ResearchAgentDetail = () => {
  const { key } = useParams();
  const [agent, setAgent] = useState(null);
  const [tab, setTab] = useState("Knowledge");
  const [items, setItems] = useState([]);
  const [itemFilter, setItemFilter] = useState("");
  const [runs, setRuns] = useState([]);
  const [sources, setSources] = useState([]);
  const [running, setRunning] = useState(false);
  const [juris, setJuris] = useState("");
  const [msg, setMsg] = useState("");
  const [persona, setPersona] = useState("");
  const [savingPersona, setSavingPersona] = useState(false);
  const [decidingId, setDecidingId] = useState(null);

  const loadAgent = useCallback(async () => {
    const a = await getResearchAgent(key);
    setAgent(a);
    setPersona(a.persona_prompt || "");
    setJuris((a.jurisdictions || ["GLOBAL"])[0]);
  }, [key]);

  const loadItems = useCallback(async () => {
    setItems(await listKnowledgeItems({ agent: key, status: itemFilter }) || []);
  }, [key, itemFilter]);

  useEffect(() => { loadAgent(); }, [loadAgent]);
  useEffect(() => { if (tab === "Knowledge") loadItems(); }, [tab, loadItems]);
  useEffect(() => {
    if (tab === "Runs") listResearchRuns(key).then((r) => setRuns(r || []));
    if (tab === "Sources") listResearchSources(key).then((s) => setSources(s || []));
  }, [tab, key]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 3000); };

  const handleRun = async () => {
    setRunning(true);
    try {
      const run = await runResearchAgent(key, { jurisdiction: juris });
      flash(`Run finished: ${run.items_proposed} proposed (${run.status}).`);
      loadAgent();
      if (tab === "Knowledge") loadItems();
      if (tab === "Runs") listResearchRuns(key).then((r) => setRuns(r || []));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      flash(detail || "Run failed.");
    } finally {
      setRunning(false);
    }
  };

  const handleItemDecide = async (id, decision) => {
    setDecidingId(id);
    try {
      if (decision === "approve") await approveKnowledgeItem(id);
      else await rejectKnowledgeItem(id);
      flash(decision === "approve" ? "Approved — live to the specialists." : "Removed from the specialists.");
      await Promise.all([loadItems(), loadAgent()]);
    } catch {
      flash("Action failed.");
    } finally {
      setDecidingId(null);
    }
  };

  const savePersona = async () => {
    setSavingPersona(true);
    try {
      await updateResearchAgent(key, { persona_prompt: persona });
      flash("Persona saved.");
    } catch {
      flash("Could not save persona.");
    } finally {
      setSavingPersona(false);
    }
  };

  if (!agent) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin/research" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-500 mb-1">
            <ArrowLeftIcon className="w-4 h-4" /> Research Desk
          </Link>
          <h1 className="text-3xl font-bold">{agent.name}</h1>
          <p className="text-sm text-gray-500 mt-1 capitalize">
            {agent.domain} · feeds {(agent.feeds_specialists || []).join(", ") || "—"} · {agent.schedule}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={juris}
            onChange={(e) => setJuris(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 text-sm"
          >
            {(agent.jurisdictions || ["GLOBAL"]).map((j) => (
              <option key={j} value={j}>{j}</option>
            ))}
          </select>
          <button
            onClick={handleRun}
            disabled={running}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50"
          >
            <PlayIcon className="w-5 h-5" />
            {running ? "Running…" : "Run now"}
          </button>
        </div>
      </div>

      {msg && (
        <div className="p-3 rounded-xl bg-indigo-900/20 border border-indigo-700 text-indigo-200 text-sm">{msg}</div>
      )}

      <Card>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-6">
          <div><p className="text-xs uppercase tracking-widest text-gray-500">Active</p><p className="text-2xl font-bold">{agent.active_items}</p></div>
          <div><p className="text-xs uppercase tracking-widest text-gray-500">Pending</p><p className="text-2xl font-bold text-amber-500">{agent.pending_items}</p></div>
          <div><p className="text-xs uppercase tracking-widest text-gray-500">Sources</p><p className="text-2xl font-bold">{agent.source_count}</p></div>
          <div><p className="text-xs uppercase tracking-widest text-gray-500">Last run</p><p className="text-sm mt-1">{fmt(agent.last_run_at)}</p></div>
        </div>
      </Card>

      {/* Sub-tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-white/10">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t
                ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Knowledge" && (
        <div className="space-y-3">
          <div className="flex gap-2">
            {["", "active", "proposed", "superseded"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setItemFilter(s)}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  itemFilter === s ? "bg-indigo-600 text-white" : "bg-gray-200 dark:bg-white/10 text-gray-600 dark:text-gray-300"
                }`}
              >
                {s || "all"}
              </button>
            ))}
          </div>
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">No items. Run the desk to propose knowledge.</p>
          ) : (
            items.map((it) => (
              <KnowledgeItemCard
                key={it.id}
                it={it}
                onDecide={handleItemDecide}
                busy={decidingId === it.id}
              />
            ))
          )}
        </div>
      )}

      {tab === "Runs" && (
        <div className="space-y-3">
          {runs.length === 0 ? (
            <p className="text-sm text-gray-500">No runs yet.</p>
          ) : (
            runs.map((r) => (
              <Card key={r.id}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge kind={r.status}>{r.status}</Badge>
                    <span className="text-sm font-semibold">{r.jurisdiction}</span>
                    <span className="text-xs text-gray-500">{r.trigger}</span>
                  </div>
                  <span className="text-xs text-gray-500">{fmt(r.started_at)}</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {r.items_proposed} proposed / {r.items_found} found · tokens {r.tokens_in}/{r.tokens_out}
                </p>
                {(r.sources_used || []).length > 0 && (
                  <div className="mt-2">
                    <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-1">Sources referenced</p>
                    <ul className="space-y-0.5">
                      {r.sources_used.slice(0, 8).map((s, i) => (
                        <li key={i} className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          • {s.title || s.url || s.type}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {r.error && <p className="text-xs text-red-500 mt-2">{r.error}</p>}
                {r.log && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer">Run log</summary>
                    <pre className="text-[11px] text-gray-500 whitespace-pre-wrap mt-1">{r.log}</pre>
                  </details>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {tab === "Sources" && (
        <div className="space-y-2">
          {sources.length === 0 ? (
            <p className="text-sm text-gray-500">No sources referenced yet.</p>
          ) : (
            sources.map((s) => (
              <Card key={s.id} className="!p-4">
                <div className="flex items-center justify-between gap-3">
                  <a href={s.url} target="_blank" rel="noreferrer" className="text-sm text-indigo-500 hover:underline truncate">
                    {s.title || s.url}
                  </a>
                  <span className="text-xs text-gray-500 whitespace-nowrap">×{s.citation_count} · {s.type}</span>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {tab === "Config" && (
        <Card>
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Researcher persona</p>
          <p className="text-xs text-gray-500 mb-2">
            This is the expert system prompt the desk uses when researching. Make it sharp.
          </p>
          <textarea
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            rows={12}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 text-sm font-mono"
          />
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            <p className="text-xs text-gray-500">
              Sources enabled: {(agent.sources_enabled || []).join(", ") || "—"} · Jurisdictions: {(agent.jurisdictions || []).join(", ")}
            </p>
            <button
              onClick={savePersona}
              disabled={savingPersona}
              className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50"
            >
              {savingPersona ? "Saving…" : "Save persona"}
            </button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ResearchAgentDetail;
